import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { t } from 'i18next';
import type { FileContent } from 'use-file-picker/dist/interfaces';

import { ArrowLeft, ArrowRight } from '@peacecover/icon';
import { ButtonToggle } from '@peacecover/component';
import useUserQuery from '../hooks/queries/useUserQuery';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import TradeMethodBottomDrawer from '../../components/drawer/TradeMethodBottomDrawer';
import SelectedPhotoCardsList from './components/SelectedPhotoCardsList';
import ImageUploadSection from './components/ImageUploadSection';
import AddTradingPhotoCardsV2 from '../../components/api/mutations/AddTradingPhotoCardsV2';
import { useBatchUpdateTradingPhotoCardsMutation } from './hooks/useBatchUpdateTradingPhotoCardsMutation';
import { useSelectedTradingCardsStore } from './store/useSelectedTradingCardsStore';
import BottomCTA from './components/BottomCTA';
import RegisterLoadingPage from './RegisterLoadingPage';
import RegisterCompletedPage from './RegisterCompletedPage';
import { useSearchParams } from 'react-router-dom';

const RegisterSalePhotoCardsDescriptionPage = () => {
  const [searchParams] = useSearchParams();
  const [description, setDescription] = useState('');
  const [imageFiles, setImageFiles] = useState<FileContent<string>[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTradeMethod, setSelectedTradeMethod] = useState<string | null>(null);
  const [isNewItem, setIsNewItem] = useState(false);
  
  const [isUploading, setIsUploading] = useState(false); // 업로드 중인지 여부
  const [isUploadCompleted, setIsUploadCompleted] = useState(false); // 업로드 완료 여부

  const { selectedPhotoCards } = useSelectedTradingCardsStore();
  const { data: user } = useUserQuery();
  const { mutateAsync: batchUpdateTradingPhotoCards } = useBatchUpdateTradingPhotoCardsMutation();

  //새롭게 생성된 트레이딩 카드일경우
  const isNewTradingPhotoCards = searchParams.get('new') === 'true';

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };

  const handleTradeMethodSelect = (method: string) => {
    setSelectedTradeMethod(method);
    setIsDrawerOpen(false);
  };

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  if (!user) {
    return null;
  }

  const buildNewTradingPhotoCardsData = () => {
    return selectedPhotoCards.map((card) => ({
      artistId: card.artistId,
      photoCard: {
        memberId: card.photoCard.memberId,
        photoCardId: card.photoCard.photoCardId,
        isNewItem
      },
      description,
      category: 'sale',
      price: card.price,
      tradeMethod: selectedTradeMethod
    }));
  };

  //새롭게 생성된 트레이딩 카드일경우 기존 데이터 취소하는 로직 비활성화
  const cancelPreviousTradingPhotoCards = async () => {
    if (isNewTradingPhotoCards) {
      return;
    }
    const cardsToCancel = selectedPhotoCards.map((card) => ({
      ...card,
      tradingPhotoCardId: card.tradingPhotoCardId,
      status: 'canceled'
    }));
    await batchUpdateTradingPhotoCards(cardsToCancel);
  };

  const onDone = async () => {
    if (!isUploading && user?.tradingUserProfile) {
      setIsUploading(true);
      try {
        const tradingPhotoCardsData = buildNewTradingPhotoCardsData();

        const response = await AddTradingPhotoCardsV2(
          user.tradingUserProfile.userProfileId,
          tradingPhotoCardsData,
          imageFiles.map((imageFile) => imageFile.content)
        );
        if (response.ok) {
          await cancelPreviousTradingPhotoCards();
          setIsUploadCompleted(true);
        }
      } catch (error) {
        console.error('Error adding trading photo cards:', error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  if (isUploading) {
    return <RegisterLoadingPage />;
  }

  if (isUploadCompleted) {
    return <RegisterCompletedPage />;
  }

  return (
    <main className="min-h-screen bg-pure-white dark:bg-background-dark pt-[calc(env(safe-area-inset-top))] pb-[calc(env(safe-area-inset-bottom))]">
      <Helmet>
        <title>{t('ConfirmNewSalesTradingPhotoCardsPage.title')}</title>
      </Helmet>
      <RegisterSalePhotoCardsDescriptionHeader isUploading={isUploading} />
      <div className="mt-[54px] px-[24px] pt-[24px] pb-[16px]">
        <h2 className="h-[18px] text-[15px] font-semibold text-text-main-lv1 leading-none">
          {t('ConfirmNewSalesTradingPhotoCardsPage.subTitleProduct')}
        </h2>
      </div>
      <SelectedPhotoCardsList user={user} />
      <div className="px-[24px] pt-[32px] pb-[7px]">
        <h2 className="h-[18px] text-[15px] font-semibold text-text-main-lv1 leading-none">
          {t('ConfirmNewSalesTradingPhotoCardsPage.subTitleDescription')}
        </h2>
      </div>
      <ImageUploadSection imageFiles={imageFiles} setImageFiles={setImageFiles} />
      <div className="px-[24px]">
        <textarea
          className="h-[120px] w-full p-[20px] text-text-main-lv2 bg-transparent resize-none text-[15px] font-medium outline-none rounded-[12px] border border-separator1 placeholder:text-placeholder-light dark:placeholder:text-placeholder-dark"
          placeholder={t('ConfirmNewSalesTradingPhotoCardsPage.descriptionPlaceholder') || ''}
          value={description}
          onChange={handleDescriptionChange}
        />
      </div>
      <NewItemToggle isNewItem={isNewItem} setIsNewItem={setIsNewItem} />
      <div className="flex items-center justify-between mx-[24px] py-[24px] h-[68px] mb-[84px] border-t border-surface-lv2">
        <p className="text-[15px] font-semibold text-text-main-lv1 leading-none">
          {t('ConfirmNewSalesTradingPhotoCardsPage.subTitleTradeMethod')}
        </p>
        <button type="button" className="flex items-center gap-[4px]" onClick={handleOpenDrawer}>
          <p className="text-[15px] font-medium text-text-main-lv3 leading-none">
            {t(`TradeMethod.${selectedTradeMethod || 'any'}`)}
          </p>
          <ArrowRight size={15} className="text-text-sub-lv1" />
        </button>
      </div>
      <BottomCTA onClick={onDone} disabled={isUploading}>
        {t('done')}
      </BottomCTA>
      {isDrawerOpen && (
        <TradeMethodBottomDrawer
          user={user}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          onSelectTradeMethod={handleTradeMethodSelect}
        />
      )}
    </main>
  );
};

export default RegisterSalePhotoCardsDescriptionPage;

const RegisterSalePhotoCardsDescriptionHeader = ({ isUploading }: { isUploading: boolean }) => {
  const handleHistoryBack = useHandleHistoryBack();
  return (
    <header className="fixed top-0 left-0 right-0 flex items-center px-[16px] py-[8px] bg-pure-white dark:bg-background-dark z-10 pt-[calc(env(safe-area-inset-top)+8px)]">
      <ArrowLeft
        className="fill-text-main-lv1 my-[2px]"
        onClick={() => {
          if (isUploading) {
            return;
          }
          handleHistoryBack();
        }}
      />
      <h1 className="h-[20px] flex items-center absolute left-1/2 transform -translate-x-1/2 text-[17px] font-bold text-text-main-lv1 my-[4px] leading-none text-center">
        {t('ConfirmNewSalesTradingPhotoCardsPage.title')}
      </h1>
    </header>
  );
};

const NewItemToggle = ({
  isNewItem,
  setIsNewItem
}: {
  isNewItem: boolean;
  setIsNewItem: (isNewItem: boolean) => void;
}) => {
  return (
    <div className="flex flex-row items-center gap-[8px] px-[20px] pt-[10px] pb-[24px]">
      <ButtonToggle
        type="check"
        size="lg"
        checked={isNewItem}
        onChange={() => setIsNewItem(!isNewItem)}
      />
      <p className="text-[15px] leading-none text-text-main-lv1">
        {t('ConfirmNewSalesTradingPhotoCardsPage.newItem')}
      </p>
    </div>
  );
};
